import { Button, MenuItem, Select, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit,
  FiSave,
  FiTrash2,
  FiUpload,
  FiZap
} from "react-icons/fi";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  TechnicalReportDeviceInterface,
  TechnicalReportImageInterface
} from "../../common/interfaces";
import { TechnicalReportImageCategory } from "../../components/enum/technicalReportEnums";
import CustomTooltip from "../../components/CustomTooltip";
import { ActionButton } from "../workOrder/workorder.styled";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle
} from "../workOrder/workorder-detail.styled";
import {
  deleteImage,
  listImages,
  removeDeviceFromReport,
  reorderImages,
  updateImage,
  updateReportDevice,
  uploadImages
} from "./actions";
import {
  FieldsGrid,
  PhotoCard,
  PhotoGrid,
  PhotoInfo,
  PhotoToolbar,
  UploadRow
} from "./technical-report.styled";

const CHECKLIST_ITEMS = [
  "Limpeza geral realizada",
  "Inspeção visual sem danos aparentes",
  "Conexões e cabeamento verificados",
  "Teste de funcionamento realizado",
  "Etiqueta/identificação conferida",
  "Registro fotográfico realizado"
];

const CONDITION_OPTIONS = ["Não verificado", "Bom", "Regular", "Ruim"];

type PhaseValue = { r: string; s: string; t: string; type?: string };
const emptyVoltagePhase = (): PhaseValue => ({ r: "", s: "", t: "", type: "AC" });
const emptyCurrentPhase = (): PhaseValue => ({ r: "", s: "", t: "" });

type BatteryData = {
  type: string;
  manufacturer: string;
  manufacture_date: string;
  quantity: string;
  capacity_ah: string;
  bank_nominal_voltage: string;
  charger_voltage: string;
  recharge_current: string;
  terminal_type: string;
};
const emptyBattery = (): BatteryData => ({
  type: "",
  manufacturer: "",
  manufacture_date: "",
  quantity: "",
  capacity_ah: "",
  bank_nominal_voltage: "",
  charger_voltage: "",
  recharge_current: "",
  terminal_type: ""
});

type ConditionData = {
  input: string;
  output: string;
  ground_neutral?: string;
  battery_bank?: string;
  bypass?: string;
  battery?: string;
};

// Definidos fora do componente — se ficassem dentro, seriam recriados a
// cada render e o React perderia o foco dos campos a cada tecla digitada.
const PhaseFieldGroup: React.FC<{
  label: string;
  value: PhaseValue;
  onChange: (next: PhaseValue) => void;
  showType?: boolean;
  disabled?: boolean;
}> = ({ label, value, onChange, showType, disabled }) => (
  <div>
    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>
      {label}
    </div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <MeasurementField label="R" width={80} value={value.r} disabled={disabled}
        onChange={(r) => onChange({ ...value, r })} />
      <MeasurementField label="S" width={80} value={value.s} disabled={disabled}
        onChange={(s) => onChange({ ...value, s })} />
      <MeasurementField label="T" width={80} value={value.t} disabled={disabled}
        onChange={(t) => onChange({ ...value, t })} />
      {showType && (
        <Select size="small" style={{ width: 90 }} value={value.type || "AC"} disabled={disabled}
          onChange={(e) => onChange({ ...value, type: e.target.value as string })}>
          <MenuItem value="AC">AC</MenuItem>
          <MenuItem value="DC">DC</MenuItem>
        </Select>
      )}
    </div>
  </div>
);

/**
 * Campo de medição (tensão, corrente, potência). Usa inputMode "decimal" em
 * vez de type="number": o teclado do celular abre numérico do mesmo jeito,
 * mas sem os defeitos do number no desktop (roda do mouse alterando o valor
 * e recusa de vírgula). O filtro impede letra num campo que vira número no
 * laudo, e aceita tanto 2,16 quanto 2.16.
 */
const MeasurementField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  width?: number;
  digitsOnly?: boolean;
  onChange: (next: string) => void;
}> = ({ label, value, disabled, width, digitsOnly, onChange }) => (
  <TextField
    size="small"
    label={label}
    value={value}
    disabled={disabled}
    style={width ? { width } : undefined}
    inputProps={{
      inputMode: digitsOnly ? "numeric" : "decimal",
      pattern: digitsOnly ? "[0-9]*" : "[0-9.,-]*"
    }}
    onChange={(e) =>
      onChange(
        e.target.value.replace(digitsOnly ? /[^0-9]/g : /[^0-9.,-]/g, "")
      )
    }
  />
);

const ConditionField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <div>
    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>
      {label}
    </div>
    <Select size="small" fullWidth value={value || "Não verificado"} disabled={disabled}
      onChange={(e) => onChange(e.target.value as string)}>
      {CONDITION_OPTIONS.map((opt) => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </Select>
  </div>
);

interface Props {
  reportId: number;
  deviceRow: TechnicalReportDeviceInterface;
  locked: boolean;
  autoSuccess: (msg: string) => void;
  autoError: (msg: string) => void;
  onRemoved: () => void;
}

const TechnicalReportDeviceBlock: React.FC<Props> = ({
  reportId,
  deviceRow,
  locked,
  autoSuccess,
  autoError,
  onRemoved
}) => {
  const handleHttpError = makeToasterErrorHandler(autoError);
  const workOrderDeviceId = deviceRow.work_order_device_id;
  const device = deviceRow.TB_WORK_ORDER_DEVICE?.TB_DEVICE;

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // dados técnicos — equipamento
  const [eqType, setEqType] = useState("");
  const [eqManufacturer, setEqManufacturer] = useState("");
  const [eqSector, setEqSector] = useState("");
  const [eqIoConfig, setEqIoConfig] = useState("");
  const [eqInputVoltage, setEqInputVoltage] = useState("");
  const [eqOutputVoltage, setEqOutputVoltage] = useState("");
  const [eqPower, setEqPower] = useState("");
  const [eqManufactureDate, setEqManufactureDate] = useState("");

  const [checklist, setChecklist] = useState<string[]>([]);

  const [nobreakEnabled, setNobreakEnabled] = useState(false);
  const [battery, setBattery] = useState<BatteryData>(emptyBattery());
  const [inputVoltage, setInputVoltage] = useState<PhaseValue>(emptyVoltagePhase());
  const [outputVoltage, setOutputVoltage] = useState<PhaseValue>(emptyVoltagePhase());
  const [bypassVoltage, setBypassVoltage] = useState<PhaseValue>(emptyVoltagePhase());
  const [inputCurrent, setInputCurrent] = useState<PhaseValue>(emptyCurrentPhase());
  const [outputCurrent, setOutputCurrent] = useState<PhaseValue>(emptyCurrentPhase());
  const [bypassCurrent, setBypassCurrent] = useState<PhaseValue>(emptyCurrentPhase());
  const [wiring, setWiring] = useState<ConditionData>({
    input: "Não verificado",
    output: "Não verificado",
    ground_neutral: "Não verificado",
    battery_bank: "Não verificado"
  });
  const [breaker, setBreaker] = useState<ConditionData>({
    input: "Não verificado",
    output: "Não verificado",
    bypass: "Não verificado",
    battery: "Não verificado"
  });
  const [logsCollected, setLogsCollected] = useState("");

  // fotos
  const [images, setImages] = useState<TechnicalReportImageInterface[]>([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadCategory, setUploadCategory] = useState("GENERAL");
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingImageId, setEditingImageId] = useState<number | null>(null);
  const [editCategory, setEditCategory] = useState("GENERAL");
  const [editCaption, setEditCaption] = useState("");
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const data = deviceRow.technical_data || {};
    setChecklist(Array.isArray(data.checklist) ? data.checklist : []);

    const eq = data.equipment || {};
    setEqType(eq.type ?? "");
    setEqManufacturer(eq.manufacturer ?? device?.device_brand ?? "");
    setEqSector(eq.sector ?? "");
    setEqIoConfig(eq.io_config ?? "");
    setEqInputVoltage(eq.input_voltage ?? "");
    setEqOutputVoltage(eq.output_voltage ?? "");
    setEqPower(eq.power ?? "");
    setEqManufactureDate(eq.manufacture_date ?? "");

    const nb = data.nobreak;
    setNobreakEnabled(!!nb);
    setBattery({
      type: nb?.battery?.type ?? "",
      manufacturer: nb?.battery?.manufacturer ?? "",
      manufacture_date: nb?.battery?.manufacture_date ?? "",
      quantity: nb?.battery?.quantity ?? "",
      capacity_ah: nb?.battery?.capacity_ah ?? "",
      bank_nominal_voltage: nb?.battery?.bank_nominal_voltage ?? "",
      charger_voltage: nb?.battery?.charger_voltage ?? "",
      recharge_current: nb?.battery?.recharge_current ?? "",
      terminal_type: nb?.battery?.terminal_type ?? ""
    });
    setInputVoltage(nb?.measurements?.input_voltage ?? emptyVoltagePhase());
    setOutputVoltage(nb?.measurements?.output_voltage ?? emptyVoltagePhase());
    setBypassVoltage(nb?.measurements?.bypass_voltage ?? emptyVoltagePhase());
    setInputCurrent(nb?.measurements?.input_current ?? emptyCurrentPhase());
    setOutputCurrent(nb?.measurements?.output_current ?? emptyCurrentPhase());
    setBypassCurrent(nb?.measurements?.bypass_current ?? emptyCurrentPhase());
    setWiring({
      input: nb?.wiring_condition?.input ?? "Não verificado",
      output: nb?.wiring_condition?.output ?? "Não verificado",
      ground_neutral: nb?.wiring_condition?.ground_neutral ?? "Não verificado",
      battery_bank: nb?.wiring_condition?.battery_bank ?? "Não verificado"
    });
    setBreaker({
      input: nb?.breaker_condition?.input ?? "Não verificado",
      output: nb?.breaker_condition?.output ?? "Não verificado",
      bypass: nb?.breaker_condition?.bypass ?? "Não verificado",
      battery: nb?.breaker_condition?.battery ?? "Não verificado"
    });
    setLogsCollected(nb?.logs_collected ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceRow.technical_report_device_id]);

  useEffect(() => {
    (async () => {
      try {
        setImagesLoading(true);
        const imgs = await listImages(reportId, deviceRow.technical_report_device_id);
        setImages(imgs);
      } catch (err) {
        handleHttpError(err);
      } finally {
        setImagesLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, [reportId, deviceRow.technical_report_device_id]);

  const buildTechnicalData = () => {
    const data: Record<string, any> = {};
    if (checklist.length) data.checklist = checklist;

    const equipment: Record<string, any> = {};
    if (eqType) equipment.type = eqType;
    if (eqManufacturer) equipment.manufacturer = eqManufacturer;
    if (eqSector) equipment.sector = eqSector;
    if (eqIoConfig) equipment.io_config = eqIoConfig;
    if (eqInputVoltage) equipment.input_voltage = eqInputVoltage;
    if (eqOutputVoltage) equipment.output_voltage = eqOutputVoltage;
    if (eqPower) equipment.power = eqPower;
    if (eqManufactureDate) equipment.manufacture_date = eqManufactureDate;
    if (Object.keys(equipment).length) data.equipment = equipment;

    if (nobreakEnabled) {
      data.nobreak = {
        battery,
        measurements: {
          input_voltage: inputVoltage,
          output_voltage: outputVoltage,
          bypass_voltage: bypassVoltage,
          input_current: inputCurrent,
          output_current: outputCurrent,
          bypass_current: bypassCurrent
        },
        wiring_condition: wiring,
        breaker_condition: breaker,
        logs_collected: logsCollected || undefined
      };
    }

    return data;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateReportDevice(reportId, workOrderDeviceId, buildTechnicalData());
      autoSuccess(`Dados técnicos de "${device?.device_name ?? "equipamento"}" salvos.`);
    } catch (err) {
      handleHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remover "${device?.device_name ?? "este equipamento"}" do relatório? As fotos dele também serão removidas.`)) {
      return;
    }
    try {
      setRemoving(true);
      await removeDeviceFromReport(reportId, workOrderDeviceId);
      autoSuccess("Equipamento removido do relatório.");
      onRemoved();
    } catch (err) {
      handleHttpError(err);
    } finally {
      setRemoving(false);
    }
  };

  const handleUploadPhotos = async () => {
    if (pendingFiles.length === 0) return;
    try {
      setUploading(true);
      const caption = uploadCaption.trim();
      const uploaded = await uploadImages(
        reportId,
        deviceRow.technical_report_device_id,
        pendingFiles,
        uploadCategory,
        caption ? pendingFiles.map(() => caption) : undefined
      );
      setImages((prev) => [...prev, ...uploaded].sort((a, b) => a.sort_order - b.sort_order));
      setPendingFiles([]);
      setUploadCaption("");
      autoSuccess("Fotos enviadas.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (locked) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (locked) return;
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (dropped.length) setPendingFiles((prev) => [...prev, ...dropped]);
  };

  const handleMove = async (imageId: number, direction: -1 | 1) => {
    const idx = images.findIndex((i) => i.technical_report_image_id === imageId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= images.length) return;
    const next = [...images];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    const items = next.map((img, i) => ({ technical_report_image_id: img.technical_report_image_id, sort_order: i }));
    try {
      const updated = await reorderImages(reportId, items);
      setImages(updated.filter((i) => i.technical_report_device_id === deviceRow.technical_report_device_id));
    } catch (err) {
      handleHttpError(err);
    }
  };

  const handleDeletePhoto = async (imageId: number) => {
    try {
      await deleteImage(reportId, imageId);
      setImages((prev) => prev.filter((i) => i.technical_report_image_id !== imageId));
      autoSuccess("Foto removida.");
    } catch (err) {
      handleHttpError(err);
    }
  };

  const startEditImage = (img: TechnicalReportImageInterface) => {
    setEditingImageId(img.technical_report_image_id);
    setEditCategory(img.category);
    setEditCaption(img.caption ?? "");
  };

  const saveEditImage = async () => {
    if (editingImageId == null) return;
    try {
      const updated = await updateImage(reportId, editingImageId, { category: editCategory, caption: editCaption });
      setImages((prev) => prev.map((i) => (i.technical_report_image_id === editingImageId ? updated : i)));
      setEditingImageId(null);
      autoSuccess("Foto atualizada.");
    } catch (err) {
      handleHttpError(err);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {device?.device_name ?? "Equipamento"}
          {device?.device_brand && (
            <span style={{ fontWeight: 400, fontSize: "0.8rem", marginLeft: 8, color: "var(--text-muted)" }}>
              {device.device_brand} / {device.device_model} · Série: {device.device_serial_number}
            </span>
          )}
        </CardTitle>
        {!locked && (
          <Button
            size="small"
            color="error"
            startIcon={<FiTrash2 />}
            disabled={removing}
            onClick={handleRemove}
          >
            Remover
          </Button>
        )}
      </CardHeader>
      <CardBody>
        <FieldsGrid>
          <TextField size="small" label="Tipo de equipamento" placeholder="Ex: Nobreak, Ar-condicionado..."
            value={eqType} disabled={locked} onChange={(e) => setEqType(e.target.value)} />
          <TextField size="small" label="Fabricante" value={eqManufacturer} disabled={locked}
            onChange={(e) => setEqManufacturer(e.target.value)} />
          <TextField size="small" label="Setor" value={eqSector} disabled={locked}
            onChange={(e) => setEqSector(e.target.value)} />
          <TextField size="small" label="Configuração entrada/saída" value={eqIoConfig} disabled={locked}
            onChange={(e) => setEqIoConfig(e.target.value)} />
          <MeasurementField label="Tensão de entrada (V)" value={eqInputVoltage} disabled={locked}
            onChange={setEqInputVoltage} />
          <MeasurementField label="Tensão de saída (V)" value={eqOutputVoltage} disabled={locked}
            onChange={setEqOutputVoltage} />
          <MeasurementField label="Potência (kVA)" value={eqPower} disabled={locked}
            onChange={setEqPower} />
          <TextField size="small" type="date" label="Data de fabricação" InputLabelProps={{ shrink: true }}
            value={eqManufactureDate} disabled={locked} onChange={(e) => setEqManufactureDate(e.target.value)} />
        </FieldsGrid>

        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>Checklist de atividades</div>
          {CHECKLIST_ITEMS.map((item) => (
            <label key={item} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", padding: "2px 0" }}>
              <input
                type="checkbox"
                disabled={locked}
                checked={checklist.includes(item)}
                onChange={() =>
                  setChecklist((prev) =>
                    prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
                  )
                }
              />
              {item}
            </label>
          ))}
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.9rem", fontWeight: 600 }}>
            <input
              type="checkbox"
              disabled={locked}
              checked={nobreakEnabled}
              onChange={(e) => setNobreakEnabled(e.target.checked)}
            />
            Equipamento é um nobreak (baterias e medições elétricas)
          </label>
        </div>

        {nobreakEnabled && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 700, margin: "12px 0 6px", display: "flex", alignItems: "center", gap: 6 }}>
              <FiZap /> Baterias
            </div>
            <FieldsGrid>
              <TextField size="small" label="Tipo" value={battery.type} disabled={locked}
                onChange={(e) => setBattery({ ...battery, type: e.target.value })} />
              <TextField size="small" label="Fabricante" value={battery.manufacturer} disabled={locked}
                onChange={(e) => setBattery({ ...battery, manufacturer: e.target.value })} />
              <TextField size="small" type="date" label="Data de fabricação" InputLabelProps={{ shrink: true }}
                value={battery.manufacture_date} disabled={locked}
                onChange={(e) => setBattery({ ...battery, manufacture_date: e.target.value })} />
              <MeasurementField label="Quantidade" digitsOnly value={battery.quantity} disabled={locked}
                onChange={(quantity) => setBattery({ ...battery, quantity })} />
              <MeasurementField label="Capacidade (Ah)" value={battery.capacity_ah} disabled={locked}
                onChange={(capacity_ah) => setBattery({ ...battery, capacity_ah })} />
              <MeasurementField label="Tensão nominal do banco (V)" value={battery.bank_nominal_voltage} disabled={locked}
                onChange={(bank_nominal_voltage) => setBattery({ ...battery, bank_nominal_voltage })} />
              <MeasurementField label="Tensão do carregador (V)" value={battery.charger_voltage} disabled={locked}
                onChange={(charger_voltage) => setBattery({ ...battery, charger_voltage })} />
              <MeasurementField label="Corrente de recarga (A)" value={battery.recharge_current} disabled={locked}
                onChange={(recharge_current) => setBattery({ ...battery, recharge_current })} />
              <TextField size="small" label="Tipo de terminal" value={battery.terminal_type} disabled={locked}
                onChange={(e) => setBattery({ ...battery, terminal_type: e.target.value })} />
            </FieldsGrid>

            <div style={{ fontSize: "0.85rem", fontWeight: 700, margin: "16px 0 6px" }}>Medições elétricas</div>
            <FieldsGrid>
              <PhaseFieldGroup label="Tensão de entrada (R/S/T)" value={inputVoltage} onChange={setInputVoltage} showType disabled={locked} />
              <PhaseFieldGroup label="Tensão de saída (R/S/T)" value={outputVoltage} onChange={setOutputVoltage} showType disabled={locked} />
              <PhaseFieldGroup label="Tensão de bypass (R/S/T)" value={bypassVoltage} onChange={setBypassVoltage} showType disabled={locked} />
              <PhaseFieldGroup label="Corrente de entrada (R/S/T)" value={inputCurrent} onChange={setInputCurrent} disabled={locked} />
              <PhaseFieldGroup label="Corrente de saída (R/S/T)" value={outputCurrent} onChange={setOutputCurrent} disabled={locked} />
              <PhaseFieldGroup label="Corrente de bypass (R/S/T)" value={bypassCurrent} onChange={setBypassCurrent} disabled={locked} />
            </FieldsGrid>

            <div style={{ fontSize: "0.85rem", fontWeight: 700, margin: "16px 0 6px" }}>Condição do cabeamento</div>
            <FieldsGrid>
              <ConditionField label="Entrada" value={wiring.input} disabled={locked} onChange={(v) => setWiring({ ...wiring, input: v })} />
              <ConditionField label="Saída" value={wiring.output} disabled={locked} onChange={(v) => setWiring({ ...wiring, output: v })} />
              <ConditionField label="Terra/Neutro" value={wiring.ground_neutral ?? ""} disabled={locked} onChange={(v) => setWiring({ ...wiring, ground_neutral: v })} />
              <ConditionField label="Banco de baterias" value={wiring.battery_bank ?? ""} disabled={locked} onChange={(v) => setWiring({ ...wiring, battery_bank: v })} />
            </FieldsGrid>

            <div style={{ fontSize: "0.85rem", fontWeight: 700, margin: "16px 0 6px" }}>Condição dos disjuntores</div>
            <FieldsGrid>
              <ConditionField label="Entrada" value={breaker.input} disabled={locked} onChange={(v) => setBreaker({ ...breaker, input: v })} />
              <ConditionField label="Saída" value={breaker.output} disabled={locked} onChange={(v) => setBreaker({ ...breaker, output: v })} />
              <ConditionField label="Bypass" value={breaker.bypass ?? ""} disabled={locked} onChange={(v) => setBreaker({ ...breaker, bypass: v })} />
              <ConditionField label="Baterias" value={breaker.battery ?? ""} disabled={locked} onChange={(v) => setBreaker({ ...breaker, battery: v })} />
            </FieldsGrid>

            <TextField fullWidth multiline minRows={2} style={{ marginTop: 16 }} label="Logs coletados"
              value={logsCollected} disabled={locked} onChange={(e) => setLogsCollected(e.target.value)} />
          </div>
        )}

        {!locked && (
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button variant="outlined" startIcon={<FiSave />} disabled={saving} onClick={handleSave}>
              Salvar dados deste equipamento
            </Button>
          </div>
        )}

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 8 }}>Fotos</div>
          {!locked && (
            <UploadRow
              $dragActive={dragActive}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Button component="label" variant="outlined">
                Selecionar fotos (ou arraste aqui)
                <input type="file" accept="image/*" hidden multiple
                  onChange={(e) => setPendingFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} />
              </Button>
              <Select size="small" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value as string)}>
                {Object.entries(TechnicalReportImageCategory).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
              <TextField size="small" label="Legenda (opcional, aplicada a todas)" value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)} />
              <Button variant="contained" startIcon={<FiUpload />} disabled={pendingFiles.length === 0 || uploading}
                onClick={handleUploadPhotos}>
                Enviar fotos ({pendingFiles.length})
              </Button>
            </UploadRow>
          )}

          {!imagesLoading && (
            <PhotoGrid>
              {images.map((img) => (
                <PhotoCard key={img.technical_report_image_id}>
                  {img.url ? (
                    <img src={img.url} alt={img.caption ?? "Foto"} />
                  ) : (
                    <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      sem preview
                    </div>
                  )}
                  {editingImageId === img.technical_report_image_id ? (
                    <PhotoInfo>
                      <Select size="small" value={editCategory} onChange={(e) => setEditCategory(e.target.value as string)}>
                        {Object.entries(TechnicalReportImageCategory).map(([key, label]) => (
                          <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                      </Select>
                      <TextField size="small" value={editCaption} onChange={(e) => setEditCaption(e.target.value)} placeholder="Legenda" />
                      <Button size="small" onClick={saveEditImage}>Salvar</Button>
                    </PhotoInfo>
                  ) : (
                    <PhotoInfo>
                      <span className="category">
                        {TechnicalReportImageCategory[img.category as keyof typeof TechnicalReportImageCategory] ?? img.category}
                      </span>
                      {img.caption && <span className="caption">{img.caption}</span>}
                    </PhotoInfo>
                  )}
                  {!locked && (
                    <PhotoToolbar>
                      <CustomTooltip title="Mover para cima">
                        <ActionButton onClick={() => handleMove(img.technical_report_image_id, -1)}>
                          <FiChevronUp size={16} />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Mover para baixo">
                        <ActionButton onClick={() => handleMove(img.technical_report_image_id, 1)}>
                          <FiChevronDown size={16} />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Editar categoria/legenda">
                        <ActionButton onClick={() => startEditImage(img)}>
                          <FiEdit size={16} color="#34d399" />
                        </ActionButton>
                      </CustomTooltip>
                      <CustomTooltip title="Excluir foto">
                        <ActionButton onClick={() => handleDeletePhoto(img.technical_report_image_id)}>
                          <FiTrash2 size={16} color="#fb7185" />
                        </ActionButton>
                      </CustomTooltip>
                    </PhotoToolbar>
                  )}
                </PhotoCard>
              ))}
            </PhotoGrid>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default TechnicalReportDeviceBlock;
